// GET /admin/resellers — paginated.
export interface ResellerSummary {
  id: string;
  organization_id: string;
  organization_name: string;
  business_type: string;
  expected_daily_volume: string;
  is_active: boolean;
  created_at: string;
  organization_count: number;
}

export interface ResellersListResponse {
  status: boolean;
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  resellers: ResellerSummary[];
}

// GET /admin/resellers/{id}
export interface ResellerOrg {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  country_code: number;
  created_at: string;
  wallet_balance: number;
  currency: string;
}

export interface ResellerDetailResponse {
  status: boolean;
  reseller: {
    id: string;
    user_id: string;
    business_type: string;
    expected_daily_volume: string;
    business_address: string;
    initial_credit_amount: number;
    preferred_payment: string;
  };
  organization: ResellerOrg;
  organizations: ResellerOrg[];
  total_wallet_balance_by_currency: Record<string, number>;
}

// GET /admin/resellers/{id}/organizations/unconnected?search=&page=&limit=
// — businesses not yet attached to this reseller, for the "Attach
// Organization" picker. country_code here is the ISO code (e.g. "NG"), not
// the numeric dial code ResellerOrg uses.
export interface UnconnectedOrg {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  country_code: string;
  created_at: string;
}

export interface UnconnectedOrgsResponse {
  status: boolean;
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  organizations: UnconnectedOrg[];
}

// POST /admin/resellers
export interface CreateResellerPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  organization_name: string;
  country_code: number;
  business_type: "individual" | "corporation" | "LLC" | "partnership";
  expected_daily_volume: "0-10k" | "10k-100k" | "100k-1million" | "1million+";
  business_address: string;
  initial_credit_amount: number;
  preferred_payment: "credit_card" | "bank_transfer" | "paypal";
  consent_terms: boolean;
  consent_antispam: boolean;
}

export const formatResellerDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const getResellerInitials = (name: string): string =>
  name
    ?.trim()
    ?.split(/\s+/)
    ?.slice(0, 2)
    ?.map((w) => w[0])
    ?.join("")
    ?.toUpperCase() || "?";

export const getActiveStyle = (isActive?: boolean | null): string =>
  isActive
    ? "bg-green-50 text-green-700 border border-green-100 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30"
    : "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";

export const BUSINESS_TYPE_OPTIONS: { value: CreateResellerPayload["business_type"]; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "corporation", label: "Corporation" },
  { value: "LLC", label: "LLC" },
  { value: "partnership", label: "Partnership" },
];

export const DAILY_VOLUME_OPTIONS: { value: CreateResellerPayload["expected_daily_volume"]; label: string }[] = [
  { value: "0-10k", label: "0 – 10k / day" },
  { value: "10k-100k", label: "10k – 100k / day" },
  { value: "100k-1million", label: "100k – 1 million / day" },
  { value: "1million+", label: "1 million+ / day" },
];

export const PAYMENT_METHOD_OPTIONS: { value: CreateResellerPayload["preferred_payment"]; label: string }[] = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "paypal", label: "PayPal" },
];
